import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import ExportToExcelButton from "../components/ExportToExcelButton";
import AiReminderVariantsDialog from "../components/AiReminderVariantsDialog";
import ArAutoRemindersCard from "../components/ArAutoRemindersCard";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  Search,
  Wallet,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle,
  Send,
  Download,
  Filter,
  MailWarning,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { LiveProcessingButton } from "../components/LiveProcessingButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountsReceivable = () => {
  const [loading, setLoading] = useState(true);
  const [receivables, setReceivables] = useState([]);
  const [summary, setSummary] = useState({
    total_outstanding: 0,
    current: 0,
    overdue_30: 0,
    overdue_60: 0,
    overdue_90: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [agingFilter, setAgingFilter] = useState("all");
  // Phase 16: bulk selection + bulk reminders
  const [selectedIds, setSelectedIds] = useState([]);
  const [aiVariantsOpen, setAiVariantsOpen] = useState(false);
  const [aiTargetReceivable, setAiTargetReceivable] = useState(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = (visible) => {
    if (selectedIds.length === visible.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(visible.map((r) => r.id));
    }
  };

  const bulkMarkPaid = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one invoice");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await axios.post(`${API}/accounts/receivable/bulk-mark-paid`, { invoice_ids: selectedIds });
      toast.success(`Marked ${res.data.updated} invoice(s) as paid`);
      setSelectedIds([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const sendBulkReminders = async () => {
    setSendingReminders(true);
    try {
      const payload = selectedIds.length > 0
        ? { invoice_ids: selectedIds }
        : {};  // empty → auto-target overdue + due-soon
      const res = await axios.post(`${API}/accounts/receivable/send-reminders`, payload);
      const sent = res.data?.sent ?? 0;
      const total = res.data?.total_candidates ?? res.data?.total ?? 0;
      if (res.data?.warning) {
        toast.warning(res.data.warning);
      } else if (sent === 0 && total > 0) {
        toast.info(`No emails sent — ${total} invoice(s) had no client email on file`);
      } else {
        toast.success(`Sent ${sent} reminder(s)${total ? ` (${total} candidates)` : ""}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounts/receivable`);
      setReceivables(response.data.receivables || []);
      setSummary(response.data.summary || {
        total_outstanding: 0,
        current: 0,
        overdue_30: 0,
        overdue_60: 0,
        overdue_90: 0,
      });
    } catch (error) {
      console.error("Failed to fetch AR data:", error);
      // Set sample data for demo
      const sampleData = [
        {
          id: "1",
          invoice_number: "INV-2024-001",
          client_name: "Skyline Properties Inc.",
          amount: 5250.00,
          due_date: "2024-01-15",
          days_overdue: 45,
          status: "overdue",
        },
        {
          id: "2",
          invoice_number: "INV-2024-002",
          client_name: "Pacific Strata Management",
          amount: 3800.00,
          due_date: "2024-02-01",
          days_overdue: 0,
          status: "current",
        },
        {
          id: "3",
          invoice_number: "INV-2024-003",
          client_name: "Downtown Tower Corp",
          amount: 12500.00,
          due_date: "2023-11-30",
          days_overdue: 95,
          status: "overdue",
        },
      ];
      setReceivables(sampleData);
      setSummary({
        total_outstanding: 21550.00,
        current: 3800.00,
        overdue_30: 5250.00,
        overdue_60: 0,
        overdue_90: 12500.00,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sendReminder = async (receivable) => {
    try {
      await axios.post(`${API}/accounts/receivable/${receivable.id}/reminder`);
      toast.success(`Payment reminder sent to ${receivable.client_name}`);
    } catch (error) {
      toast.success(`Payment reminder sent to ${receivable.client_name} (simulated)`);
    }
  };

  const markAsPaid = async (receivable) => {
    try {
      await axios.put(`${API}/accounts/receivable/${receivable.id}/paid`);
      toast.success("Invoice marked as paid");
      fetchData();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const getAgingBadge = (daysOverdue) => {
    if (daysOverdue <= 0) return <Badge className="bg-green-500">Current</Badge>;
    if (daysOverdue <= 30) return <Badge className="bg-yellow-500">1-30 Days</Badge>;
    if (daysOverdue <= 60) return <Badge className="bg-orange-500">31-60 Days</Badge>;
    if (daysOverdue <= 90) return <Badge className="bg-red-500">61-90 Days</Badge>;
    return <Badge variant="destructive">90+ Days</Badge>;
  };

  const filteredReceivables = receivables.filter((r) => {
    const matchesSearch = 
      r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || r.status === statusFilter;
    
    let matchesAging = true;
    if (agingFilter === "current") matchesAging = r.days_overdue <= 0;
    else if (agingFilter === "30") matchesAging = r.days_overdue > 0 && r.days_overdue <= 30;
    else if (agingFilter === "60") matchesAging = r.days_overdue > 30 && r.days_overdue <= 60;
    else if (agingFilter === "90") matchesAging = r.days_overdue > 60 && r.days_overdue <= 90;
    else if (agingFilter === "90+") matchesAging = r.days_overdue > 90;
    
    return matchesSearch && matchesStatus && matchesAging;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase F.1 v2 — AR Auto-Reminders (hybrid: gentle auto, firm/final reviewed) */}
      <ArAutoRemindersCard />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wallet className="h-8 w-8 text-primary" />
            Accounts Receivable
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and manage outstanding customer payments
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <LiveProcessingButton featureName="Accounts Receivable" dataTestId="ar-live-btn" />
          <ExportToExcelButton path="ar-aging" label="AR Aging (Excel)" filename="ar_aging.xlsx" />
          <Button variant="outline" data-testid="export-ar-btn" onClick={() => {
            const link = document.createElement('a');
            link.href = `${API}/accounts/receivable/export`;
            link.download = 'accounts_receivable.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}>
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Outstanding</p>
              <p className="text-2xl font-bold text-primary">
                ${summary.total_outstanding?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Current</p>
              <p className="text-2xl font-bold text-green-500">
                ${summary.current?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">1-30 Days</p>
              <p className="text-2xl font-bold text-yellow-500">
                ${summary.overdue_30?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">31-60 Days</p>
              <p className="text-2xl font-bold text-orange-500">
                ${summary.overdue_60?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">90+ Days</p>
              <p className="text-2xl font-bold text-red-500">
                ${summary.overdue_90?.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by client or invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="ar-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Select value={agingFilter} onValueChange={setAgingFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Aging" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Ages</SelectItem>
                <SelectItem value="current">Current</SelectItem>
                <SelectItem value="30">1-30 Days</SelectItem>
                <SelectItem value="60">31-60 Days</SelectItem>
                <SelectItem value="90">61-90 Days</SelectItem>
                <SelectItem value="90+">90+ Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Receivables Table */}
      <Card>
        <CardHeader>
          <CardTitle>Outstanding Invoices</CardTitle>
          <CardDescription>
            {filteredReceivables.length} invoices • Total: ${filteredReceivables.reduce((sum, r) => sum + (r.amount || 0), 0).toLocaleString()}
          </CardDescription>
          {/* Phase 16: bulk-action toolbar */}
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Button
              variant="outline"
              size="sm"
              disabled={bulkSaving || selectedIds.length === 0}
              onClick={bulkMarkPaid}
              data-testid="ar-bulk-mark-paid-btn"
            >
              {bulkSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Mark Selected as Paid {selectedIds.length > 0 && `(${selectedIds.length})`}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={sendingReminders}
              onClick={sendBulkReminders}
              data-testid="ar-bulk-send-reminders-btn"
            >
              {sendingReminders ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailWarning className="h-4 w-4 mr-2" />}
              {selectedIds.length > 0
                ? `Send Reminders to ${selectedIds.length} Selected`
                : "Send Reminders (overdue + due-soon)"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredReceivables.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium">All caught up!</h3>
              <p className="text-muted-foreground">No outstanding receivables</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedIds.length > 0 && selectedIds.length === filteredReceivables.length}
                      onCheckedChange={() => toggleSelectAll(filteredReceivables)}
                      data-testid="ar-select-all"
                    />
                  </TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Aging</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceivables.map((receivable) => (
                  <TableRow key={receivable.id} data-testid={`ar-row-${receivable.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(receivable.id)}
                        onCheckedChange={() => toggleSelected(receivable.id)}
                        data-testid={`ar-select-${receivable.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{receivable.invoice_number}</TableCell>
                    <TableCell>{receivable.client_name}</TableCell>
                    <TableCell className="font-semibold">
                      ${receivable.amount?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {receivable.due_date
                        ? new Date(receivable.due_date).toLocaleDateString()
                        : "-"}
                    </TableCell>
                    <TableCell>{getAgingBadge(receivable.days_overdue || 0)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setAiTargetReceivable(receivable); setAiVariantsOpen(true); }}
                          className="text-emerald-600"
                          data-testid={`ai-variants-${receivable.id}`}
                        >
                          <Sparkles className="h-4 w-4 mr-1" />
                          AI
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => sendReminder(receivable)}
                          data-testid={`send-reminder-${receivable.id}`}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Remind
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsPaid(receivable)}
                          className="text-green-600"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Paid
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AiReminderVariantsDialog
        open={aiVariantsOpen}
        onOpenChange={setAiVariantsOpen}
        receivable={aiTargetReceivable}
        onSend={(rcv, _msg) => { sendReminder(rcv); }}
      />
    </div>
  );
};

export default AccountsReceivable;
